import React from 'react';
import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import { Occupation } from '../../SalaryCalculator/types';
import numberFormatter from '@/utils/numberFormatter';
import { useCalculatorContext } from '../contexts';
import { calculateIncreasePercentage } from '../../SalaryCalculator/utils/salaryDataProcessor';

interface OccupationCardProps {
  occupation: Occupation;
  isSelected: boolean;
  index: number;
  onSelect: (occupation: Occupation) => void;
  employmentChange?: string;
}

const OccupationCard: React.FC<OccupationCardProps> = ({
  occupation,
  isSelected,
  index,
  onSelect,
}) => {
  const { currencyFormat } = numberFormatter();
  const { getCalculatorValue, correlationId } = useCalculatorContext();
  const currentSalary: number =
    Number(getCalculatorValue('currentSalary')) || 0;

  const percentChange =
    currentSalary > 0 && occupation.salary && occupation.salary > 0
      ? Math.round(
          calculateIncreasePercentage(currentSalary, occupation.salary),
        )
      : null;
  const handleCardClick = () => {
    // Fire the element clicked event first - use the same correlation ID from the ProductClicked event
    tagularEvent('ElementClicked', {
      correlationId: correlationId || uuid(), // Use stored correlation ID or fallback to new UUID
      webElement: {
        elementType: 'button',
        location: 'salary-calculator',
        name: 'career-card',
        text: occupation?.title,
        position: (index + 1).toString(),
      },
    });

    onSelect(occupation);
  };

  return (
    <div
      className={`flex flex-col justify-between bg-white rounded-lg border py-3 px-4 hover:cursor-pointer hover:shadow-md transition-shadow duration-200 overflow-hidden ${
        isSelected
          ? 'outline outline-2 outline-primary-500 outline-offset-[-2px]'
          : ''
      }`}
      onClick={handleCardClick}
      data-occupation-code={occupation.onetSocCode}
    >
      <div className='flex items-center mb-3 min-w-0 w-full'>
        <div className='font-body-18 font-bold whitespace-normal break-normal w-full max-w-full overflow-hidden'>
          {occupation.title}
        </div>
      </div>

      <div className='flex flex-row flex-wrap items-center'>
        <div className='flex-1'>
          <div className='text-gray-600 font-body-14'>Avg. Annual Salary</div>
          <div className='flex items-baseline xl:flex-row flex-col font-body-20'>
            {occupation.salary && occupation.salary > 0 && (
              <div className='font-bold'>
                {currencyFormat?.format(occupation.salary)}
              </div>
            )}
            {percentChange !== null && percentChange !== 0 && (
              <div
                className={`xl:ml-auto flex items-center font-bold ${
                  percentChange > 0 ? 'text-[--success]' : 'text-[--error]'
                }`}
                aria-label={`Potential salary ${
                  percentChange > 0 ? 'increase' : 'decrease'
                } of ${Math.abs(percentChange)}%`}
              >
                <span>
                  {percentChange < 0
                    ? `-${Math.abs(percentChange)}`
                    : percentChange}
                  %
                </span>
                <span className='ml-1 leading-none' aria-hidden='true'>
                  {percentChange > 0 ? '▲' : '▼'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationCard;
